import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Timer } from '@sorare/core/src/atoms/icons/Timer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { AnimatedDiamond } from '@sorare/core/src/components/rivals/branded/AnimatedDiamond';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { HexColorOnly, RGBColor } from '@sorare/core/src/style/types';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';
import { BackgroundAligned } from '@sorare/gameplay/src/components/rivals/GridBackground/BackgroundAligned';

import { Layout_footballRivalsCurrentManager } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true })`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  overflow: hidden;
  @media ${laptopAndAbove} {
    width: 444px;
    height: 728px;
  }
`;
const Background = styled.div`
  --color: var(--c-rgb-rivals-win);

  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(
      to bottom,
      var(--c-neutral-100) 0%,
      transparent 40%,
      transparent 60%,
      var(--c-neutral-100) 100%
    ),
    rgba(var(--color), 0.5);
`;

type Props = {
  patternColor: HexColorOnly;
  diamondColor: RGBColor;
  manager: Layout_footballRivalsCurrentManager;
  children: ReactNode;
};
export const Layout = ({
  diamondColor,
  patternColor,
  manager,
  children,
}: Props) => {
  const seasonEndsAt = manager.currentLeaderboard?.season.endDate;
  return (
    <Root>
      <Background style={{ '--color': diamondColor } as CSSProperties} />
      <GridBackground height="100%" color={patternColor}>
        <BackgroundAligned horizontalDelta={0} verticalDelta={-3}>
          {seasonEndsAt && (
            <Vertical gap={0.5} center>
              <LabelM color="var(--c-neutal-700)" bold>
                <FormattedMessage
                  id="RankChange.seasonEndsIn"
                  defaultMessage="Season ends in"
                />
              </LabelM>
              <LabelM color="var(--c-neutal-700)" bold>
                <Horizontal gap={0.5} center>
                  <Timer color="var(--c-rivals-reputation)" />
                  <TimeLeft time={seasonEndsAt} />
                </Horizontal>
              </LabelM>
            </Vertical>
          )}
        </BackgroundAligned>
        <BackgroundAligned horizontalDelta={0} verticalDelta={-2}>
          <AnimatedDiamond fill={diamondColor} width={320} height={320}>
            {children}
          </AnimatedDiamond>
        </BackgroundAligned>
      </GridBackground>
    </Root>
  );
};

Layout.fragments = {
  footballRivalsCurrentManager: gql`
    fragment Layout_footballRivalsCurrentManager on FootballRivalsCurrentManager {
      id
      currentLeaderboard {
        id
        season {
          slug
          id
          endDate
        }
      }
    }
  ` as TypedDocumentNode<Layout_footballRivalsCurrentManager>,
};
